import { useCallback, useEffect, useRef, useState } from "react";
import MD from "../data/mdMap.json";

const MIN_K = 1;
const MAX_K = 9;
const HIGHLIGHT_ZOOM = 5;
const WHEEL_ZOOM_IN = 1.25;
const WHEEL_ZOOM_OUT = 0.8;
const DRAG_CLICK_THRESHOLD = 8;

// Default map view — framed to show BOTH service hubs (Baltimore north +
// Temple Hills south) and the connected coverage band between them.
const DEFAULT_K = 1.85;
const DEFAULT_CENTER = { x: 608, y: 258 }; // midpoint between the two hubs

const clamp = (value, low, high) => Math.min(high, Math.max(low, value));

export function computeInitialView(highlightSlug) {
  if (highlightSlug) {
    const city = MD.cities.find((item) => item.slug === highlightSlug);
    if (city) {
      return {
        k: HIGHLIGHT_ZOOM,
        tx: MD.width / 2 - city.x * HIGHLIGHT_ZOOM,
        ty: MD.height / 2 - city.y * HIGHLIGHT_ZOOM,
      };
    }
  }
  return {
    k: DEFAULT_K,
    tx: MD.width / 2 - DEFAULT_CENTER.x * DEFAULT_K,
    ty: MD.height / 2 - DEFAULT_CENTER.y * DEFAULT_K,
  };
}

function clientToSvg(svg, clientX, clientY) {
  const rect = svg.getBoundingClientRect();
  return [
    ((clientX - rect.left) * MD.width) / rect.width,
    ((clientY - rect.top) * MD.height) / rect.height,
  ];
}

function zoomedView(view, px, py, factor) {
  const nk = clamp(view.k * factor, MIN_K, MAX_K);
  const scale = nk / view.k;
  return { k: nk, tx: px - (px - view.tx) * scale, ty: py - (py - view.ty) * scale };
}

function pinchStateFrom(pointers) {
  const pts = [...pointers.values()].slice(0, 2);
  const dx = pts[0].x - pts[1].x;
  const dy = pts[0].y - pts[1].y;
  return {
    dist: Math.hypot(dx, dy) || 1,
    mx: (pts[0].x + pts[1].x) / 2,
    my: (pts[0].y + pts[1].y) / 2,
  };
}

function pinchedView(view, svg, prev, next) {
  const rect = svg.getBoundingClientRect();
  const panDx = ((next.mx - prev.mx) * MD.width) / rect.width;
  const panDy = ((next.my - prev.my) * MD.height) / rect.height;
  const px = ((next.mx - rect.left) * MD.width) / rect.width;
  const py = ((next.my - rect.top) * MD.height) / rect.height;
  const panned = { ...view, tx: view.tx + panDx, ty: view.ty + panDy };
  return zoomedView(panned, px, py, next.dist / prev.dist);
}

// Owns all pan / pinch / wheel-zoom gesture state for the Maryland SVG map.
export default function useMapView(highlightSlug) {
  const svgRef = useRef(null);
  const pointers = useRef(new Map());
  const pinch = useRef(null);
  const drag = useRef(null);
  const lastDrag = useRef(0);
  const [view, setView] = useState(() => computeInitialView(highlightSlug));

  const zoomAtCenter = useCallback((factor) => {
    setView((current) => zoomedView(current, MD.width / 2, MD.height / 2, factor));
  }, []);

  const resetView = useCallback(() => setView(computeInitialView(highlightSlug)), [highlightSlug]);

  // Wheel zoom (desktop) — attached natively so preventDefault works on a passive-by-default event.
  useEffect(() => {
    const svg = svgRef.current;
    const handleWheel = (event) => {
      event.preventDefault();
      const [px, py] = clientToSvg(svg, event.clientX, event.clientY);
      const factor = event.deltaY < 0 ? WHEEL_ZOOM_IN : WHEEL_ZOOM_OUT;
      setView((current) => zoomedView(current, px, py, factor));
    };
    svg.addEventListener("wheel", handleWheel, { passive: false });
    return () => svg.removeEventListener("wheel", handleWheel);
  }, []);

  // Capture lazily — capturing on pointerdown would redirect the synthesized
  // click to the SVG and break click navigation on the city pins.
  const capturePointer = (pointerId) => {
    const svg = svgRef.current;
    if (svg && !svg.hasPointerCapture(pointerId)) {
      svg.setPointerCapture(pointerId);
    }
  };

  const onPointerDown = (event) => {
    pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
    if (pointers.current.size === 2) {
      // Two-finger pinch — capture both pointers so the gesture survives leaving the SVG.
      pointers.current.forEach((_, pointerId) => capturePointer(pointerId));
      pinch.current = pinchStateFrom(pointers.current);
      drag.current = null;
    } else if (pointers.current.size === 1) {
      drag.current = { x: event.clientX, y: event.clientY, moved: 0 };
    }
  };

  const movePinch = () => {
    const next = pinchStateFrom(pointers.current);
    const svg = svgRef.current;
    const prev = pinch.current;
    setView((current) => pinchedView(current, svg, prev, next));
    pinch.current = next;
  };

  const moveDrag = (event) => {
    const rect = svgRef.current.getBoundingClientRect();
    const dx = ((event.clientX - drag.current.x) * MD.width) / rect.width;
    const dy = ((event.clientY - drag.current.y) * MD.height) / rect.height;
    drag.current.moved += Math.abs(event.clientX - drag.current.x) + Math.abs(event.clientY - drag.current.y);
    if (drag.current.moved >= DRAG_CLICK_THRESHOLD) {
      capturePointer(event.pointerId);
    }
    drag.current.x = event.clientX;
    drag.current.y = event.clientY;
    setView((current) => ({ ...current, tx: current.tx + dx, ty: current.ty + dy }));
  };

  const onPointerMove = (event) => {
    if (!pointers.current.has(event.pointerId)) return;
    pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
    if (pointers.current.size >= 2 && pinch.current) {
      movePinch();
    } else if (pointers.current.size === 1 && drag.current) {
      moveDrag(event);
    }
  };

  const onPointerUp = (event) => {
    if (drag.current) lastDrag.current = drag.current.moved || 0;
    pointers.current.delete(event.pointerId);
    if (pointers.current.size < 2) pinch.current = null;
    if (pointers.current.size === 0) {
      drag.current = null;
    } else if (pointers.current.size === 1) {
      // Resume single-finger drag from the remaining pointer
      const remaining = [...pointers.current.values()][0];
      drag.current = { x: remaining.x, y: remaining.y, moved: 0 };
    }
  };

  const wasDragged = useCallback(() => lastDrag.current >= DRAG_CLICK_THRESHOLD, []);

  return {
    svgRef,
    view,
    pointerHandlers: { onPointerDown, onPointerMove, onPointerUp, onPointerCancel: onPointerUp },
    zoomAtCenter,
    resetView,
    wasDragged,
  };
}
